<script setup>
defineOptions({ name: "SupportPage" });
import { onMounted, onUnmounted, ref } from "vue";
import {
  PhoneIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
} from "@heroicons/vue/24/outline";
import { useSeo } from "@/composables/useSeo";
import SupportHero from "@/components/support/SupportHero.vue";
import FaqPanel from "@/components/ui/FAQ.vue";
import SupportSubnav from "@/components/support/SupportSubnav.vue";
import SupportChamadoSection from "@/components/support/SupportChamadoSection.vue";
import SupportRemotoSection from "@/components/support/SupportRemotoSection.vue";
import SupportHorariosSection from "@/components/support/SupportHorariosSection.vue";
import SupportNeedHelpSection from "@/components/support/SupportNeedHelpSection.vue";
import heroBg from "@/assets/hero-suporte.webp";

useSeo({
  title: "Suporte | Kali Sistemas",
  description:
    "Central de suporte da Kali Sistemas. Fale por WhatsApp, telefone ou e-mail, abra um chamado, use o suporte remoto e consulte horários de atendimento.",
  image: heroBg,
});

const portalUrl = import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";
const portalAtendimentoUrl = `${portalUrl}/atendimento`;
const whatsAppLink = "https://wa.me/5579991389415?text=Ol%C3%A1%2C%20preciso%20de%20suporte";
const phoneLink = "tel:+5579991389415";
const emailLink = "mailto:suporte@kalisistemas.com.br";

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

const canais = [
  {
    icon: null,
    titulo: "WhatsApp",
    texto: "Fale direto com o time de suporte pelo WhatsApp.",
    ctaLabel: "Chamar no WhatsApp",
    href: whatsAppLink,
    external: true,
  },
  {
    icon: PhoneIcon,
    titulo: "Telefone",
    texto: "(79) 99138-9415 — atendimento em horário comercial.",
    ctaLabel: "Ligar agora",
    href: phoneLink,
    external: false,
  },
  {
    icon: EnvelopeIcon,
    titulo: "E-mail",
    texto: "suporte@kalisistemas.com.br — pra quem prefere registrar por escrito.",
    ctaLabel: "Enviar e-mail",
    href: emailLink,
    external: false,
  },
  {
    icon: ComputerDesktopIcon,
    titulo: "Portal do Cliente",
    texto: "Abra e acompanhe chamados, e consulte a base de conhecimento.",
    ctaLabel: "Acessar portal",
    href: portalUrl,
    external: true,
  },
];

const faqItems = [
  {
    question: "Como abro um chamado de suporte?",
    answer:
      "Pelo Portal do Cliente ou pelo formulário de contato. Se preferir, você também pode falar direto pelo WhatsApp ou telefone.",
  },
  {
    question: "Qual o prazo de resposta?",
    answer:
      "Depende da prioridade: até 2 horas para urgência alta, 4 horas para média e 1 dia útil para baixa, dentro do horário de atendimento.",
  },
  {
    question: "Vocês fazem suporte remoto?",
    answer:
      "Sim. Quando necessário, o analista orienta o acesso via AnyDesk ou RustDesk para resolver o problema diretamente na sua máquina.",
  },
  {
    question: "Onde encontro tutoriais e manuais do sistema?",
    answer:
      "A base de conhecimento fica disponível dentro do Portal do Cliente, com artigos organizados por assunto.",
  },
  {
    question: "Qual o horário de atendimento?",
    answer:
      "Segunda a sexta, das 8h às 18h, e sábado das 8h às 12h. Aos domingos e feriados não há atendimento.",
  },
];

const navLinks = [
  { label: "Canais", href: "#canais" },
  { label: "Chamado", href: "#chamado" },
  { label: "Remoto", href: "#remoto" },
  { label: "Horários", href: "#horarios" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#canais");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#canais";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, {
    passive: true,
  });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});
</script>

<template>
  <div class="min-h-screen bg-white">
    <SupportHero :image="heroBg" />

    <SupportSubnav
      :nav-links="navLinks"
      :active-nav="activeNav"
      @nav-click="handleNavClick"
    />

    <!-- Canais de atendimento -->
    <section id="canais" class="py-16 bg-slate-50 scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Canais de atendimento
          </h2>
          <p class="mt-4 text-lg leading-8 text-gray-600">
            Fale com a gente pelo canal que for mais rápido pra você.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <a
            v-for="canal in canais"
            :key="canal.titulo"
            v-reveal
            :href="canal.href"
            :target="canal.external ? '_blank' : undefined"
            :rel="canal.external ? 'noopener noreferrer' : undefined"
            class="flex flex-col p-7 bg-white rounded-3xl shadow-sm transition hover:-translate-y-1 hover:shadow-md"
          >
            <svg
              v-if="canal.titulo === 'WhatsApp'"
              class="w-9 h-9 text-kali-blue-600"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="none"
            >
              <path
                d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2zM12.05 20.21c-1.5 0-2.97-.39-4.26-1.15l-.3-.18-3.16.83.84-3.07-.2-.31a8.208 8.208 0 01-1.26-4.41c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.183 8.183 0 012.41 5.83c.02 4.54-3.68 8.23-8.22 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43s.17-.25.25-.41c.08-.17.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.76.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18s-.22-.16-.47-.28z"
              />
            </svg>
            <component v-else :is="canal.icon" class="w-9 h-9 text-kali-blue-600" />
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ canal.titulo }}</h3>
            <p class="flex-1 mt-2 text-sm leading-relaxed text-gray-600">
              {{ canal.texto }}
            </p>
            <span class="mt-4 text-sm font-semibold text-kali-blue-600">
              {{ canal.ctaLabel }} →
            </span>
          </a>
        </div>
      </div>
    </section>

    <SupportChamadoSection :portal-url="portalUrl" />
    <SupportRemotoSection />
    <SupportHorariosSection />

    <!-- Dúvidas frequentes -->
    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        title="Dúvidas sobre o suporte"
        :items="faqItems"
        cta-label="Falar com o time"
        :cta-href="portalAtendimentoUrl"
        description="Respostas rápidas sobre como funciona o suporte da Kali. Se preferir, fale direto com a gente."
        section-class="bg-slate-50"
      />
    </section>

    <SupportNeedHelpSection :whats-app-link="whatsAppLink" :portal-url="portalAtendimentoUrl" />
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
